import type {
  KeybindingCommandId,
  KeybindingScope,
} from '../model/keybindingTypes';

export type KeybindingHandlerResult = boolean | void | Promise<boolean | void>;
export type KeybindingHandler = () => KeybindingHandlerResult;
export type KeybindingHandlers = Partial<Record<KeybindingCommandId, KeybindingHandler>>;
export type KeybindingHandlersRef = { current: KeybindingHandlers };

export type KeybindingEditor = {
  hasTextFocus: () => boolean;
  getDomNode?: () => HTMLElement | null;
  getContainerDomNode?: () => HTMLElement | null;
};

export type KeybindingTargetRegistration = {
  readonly scope: KeybindingScope;
  readonly tabId?: string;
  readonly handlers: KeybindingHandlers | KeybindingHandlersRef;
  readonly getEditor?: () => KeybindingEditor | null;
  readonly getRootElement?: () => Element | null;
  readonly canExecute?: (commandId: KeybindingCommandId) => boolean;
};

type StoredRegistration = KeybindingTargetRegistration & {
  readonly token: symbol;
  readonly order: number;
  lastFocusedOrder: number;
};

export type ResolvedKeybindingTarget = {
  readonly scope: KeybindingScope;
  readonly tabId?: string;
  readonly hasHandler: (commandId: KeybindingCommandId) => boolean;
  readonly run: (commandId: KeybindingCommandId) => boolean;
  readonly canExecute: (commandId: KeybindingCommandId) => boolean;
  readonly getEditor: (commandId?: KeybindingCommandId) => KeybindingEditor | null;
  readonly getRootElement: (commandId?: KeybindingCommandId) => Element | null;
};

const registrations = new Map<symbol, StoredRegistration>();
let nextOrder = 0;
let nextFocusedOrder = 0;

function readHandlers(registration: StoredRegistration): KeybindingHandlers {
  return 'current' in registration.handlers
    ? registration.handlers.current
    : registration.handlers;
}

function ownerFor(
  entries: readonly StoredRegistration[],
  commandId: KeybindingCommandId,
): StoredRegistration | undefined {
  return [...entries]
    .reverse()
    .find((entry) => Boolean(readHandlers(entry)[commandId]));
}

function firstValue<T>(
  entries: readonly StoredRegistration[],
  commandId: KeybindingCommandId | undefined,
  read: (entry: StoredRegistration) => T | null | undefined,
): T | null {
  const owner = commandId ? ownerFor(entries, commandId) : undefined;
  const ordered = owner ? [owner, ...entries.filter((entry) => entry !== owner)] : entries;
  for (const entry of ordered) {
    const value = read(entry);
    if (value) return value;
  }
  return null;
}

export function registerKeybindingTarget(
  registration: KeybindingTargetRegistration,
): () => void {
  if (registration.scope === 'global' && registration.tabId !== undefined) {
    throw new Error('Global keybinding targets must not have a tabId.');
  }
  // SqlMonaco also mounts outside workspace tabs (for example Add Tile), so
  // sql-editor targets are selected by the focused editor instance below.
  if (registration.scope !== 'global' && registration.scope !== 'sql-editor' && !registration.tabId) {
    throw new Error('Scoped keybinding targets require a tabId.');
  }
  const token = Symbol('keybinding-target');
  registrations.set(token, {
    ...registration,
    token,
    order: nextOrder++,
    lastFocusedOrder: -1,
  });
  return () => registrations.delete(token);
}

/** Remember the editor that most recently held text focus for chrome shortcuts. */
export function noteKeybindingTargetFocus(editor: KeybindingEditor): void {
  for (const registration of registrations.values()) {
    if (registration.scope === 'sql-editor' && registration.getEditor?.() === editor) {
      registration.lastFocusedOrder = nextFocusedOrder++;
      return;
    }
  }
}

export function isKeybindingEditorVisible(editor: KeybindingEditor | null): boolean {
  const node = editor?.getDomNode?.() ?? editor?.getContainerDomNode?.() ?? null;
  return Boolean(node && (node.getClientRects().length > 0 || node.offsetParent !== null));
}

function resolveSqlEditorRegistration(
  entries: readonly StoredRegistration[],
): StoredRegistration | null {
  const focused = [...entries]
    .reverse()
    .find((entry) => entry.getEditor?.()?.hasTextFocus() === true);
  if (focused) {
    focused.lastFocusedOrder = nextFocusedOrder++;
    return focused;
  }
  return [...entries]
    .filter((entry) => entry.lastFocusedOrder >= 0 &&
      isKeybindingEditorVisible(entry.getEditor?.() ?? null))
    .sort((left, right) => right.lastFocusedOrder - left.lastFocusedOrder)[0] ?? null;
}

export function resolveActiveTarget(
  scope: KeybindingScope,
  activeTabId: string | null,
): ResolvedKeybindingTarget | null {
  let entries = [...registrations.values()]
    .filter((entry) => entry.scope === scope && (
      scope === 'global' || scope === 'sql-editor'
        ? entry.tabId === undefined
        : entry.tabId === activeTabId
    ))
    .sort((left, right) => left.order - right.order);
  if (scope === 'sql-editor') {
    const focusedEntry = resolveSqlEditorRegistration(entries);
    entries = focusedEntry ? [focusedEntry] : [];
  }
  if (entries.length === 0) return null;

  return {
    scope,
    ...(scope !== 'global' && activeTabId ? { tabId: activeTabId } : {}),
    hasHandler: (commandId) => Boolean(ownerFor(entries, commandId)),
    run: (commandId) => {
      const handler = ownerFor(entries, commandId);
      if (!handler) return false;
      return readHandlers(handler)[commandId]?.() !== false;
    },
    canExecute: (commandId) => ownerFor(entries, commandId)?.canExecute?.(commandId) !== false,
    getEditor: (commandId) => firstValue(entries, commandId, (entry) => entry.getEditor?.()),
    getRootElement: (commandId) => firstValue(entries, commandId, (entry) => entry.getRootElement?.()),
  };
}

/**
 * The root of every scoped target of the given tabs, plus the resolved SQL
 * editor's, in ONE pass over the registrations (review FR3): for each scope
 * and tab, the first root in registration order — what
 * `resolveActiveTarget(scope, tabId)?.getRootElement()` returns — so the tab
 * focus policy can tell another scoped surface from application chrome.
 */
export function scopedTargetRoots(tabIds: ReadonlySet<string>): Element[] {
  const firstRoots = new Map<string, Element>();
  const sqlEditors: StoredRegistration[] = [];
  // Map iteration follows insertion, which is registration order.
  for (const entry of registrations.values()) {
    if (entry.scope === 'global') continue;
    if (entry.scope === 'sql-editor') {
      if (entry.tabId === undefined) sqlEditors.push(entry);
      continue;
    }
    const key = `${entry.scope}:${entry.tabId}`;
    if (!entry.tabId || !tabIds.has(entry.tabId) || firstRoots.has(key)) continue;
    const root = entry.getRootElement?.();
    if (root) firstRoots.set(key, root);
  }
  const sqlEditorRoot = resolveSqlEditorRegistration(sqlEditors)?.getRootElement?.();
  return [...new Set([...firstRoots.values(), ...(sqlEditorRoot ? [sqlEditorRoot] : [])])];
}

export function clearKeybindingTargetsForTests(): void {
  registrations.clear();
  nextOrder = 0;
  nextFocusedOrder = 0;
}
